'use client';

import type { FC } from 'react';
import { getCookie, setCookie } from 'cookies-next/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DEFAULT_LOCALE } from '@/core/constants';
import {
  isCountrySelectableInLanguageChanger,
  isStaticallySelectableCountry,
  resolveLocaleForDetectedCountry,
} from '@/core/constants/browse-only-resolution';
import useUserDefaultAddress from '@/core/hooks/use-user-default-address';
import { useRouter } from '@/core/i18n/routing';
import useOrganizationsQuery from '@/core/queries/eva/use-organizations-query';
import useDetectedCountry from '@/ui/hooks/use-detected-country';
import useLocaleParams from '@/ui/hooks/use-locale-params';
import CountryDetectionModal from './CountryDetectionModal';

const DISMISSED_COOKIE_NAME = 'countryDetectionDismissed';
const COUNTRY_EXPLICITLY_SELECTED = 'countryExplicitlySelected';
const DISMISSED_COOKIE_MAX_AGE = 60 * 60 * 24 * 7; // 7 days

const CountryDetectionModalWrapper: FC = () => {
  const { country: currentCountryCode = '' } = useLocaleParams();
  const router = useRouter();
  const detectedCountryCode = useDetectedCountry();
  const { data: organizations, isPending: isOrganizationsPending } =
    useOrganizationsQuery();
  const evaCountryIds = useMemo(
    () => organizations?.map(org => org.countryId) ?? [],
    [organizations],
  );
  const [shouldShow, setShouldShow] = useState(false);

  const { data: defaultShippingAddress } = useUserDefaultAddress({
    defaultShippingAddress: true,
  });

  const userShippingCountry = defaultShippingAddress?.countryCode ?? null;

  const targetCountryCode = useMemo(() => {
    if (
      userShippingCountry &&
      userShippingCountry.toUpperCase() !== currentCountryCode.toUpperCase()
    ) {
      return userShippingCountry.toUpperCase();
    }
    return detectedCountryCode?.toUpperCase() ?? null;
  }, [userShippingCountry, detectedCountryCode, currentCountryCode]);

  useEffect(() => {
    if (!targetCountryCode) {
      setShouldShow(false);
      return;
    }

    const isDismissed = getCookie(DISMISSED_COOKIE_NAME);
    if (isDismissed) {
      setShouldShow(false);
      return;
    }

    const explicitlySelected = getCookie(COUNTRY_EXPLICITLY_SELECTED);
    if (explicitlySelected) {
      setShouldShow(false);
      return;
    }

    const targetDiffersFromCurrent =
      targetCountryCode.toUpperCase() !== currentCountryCode.toUpperCase();

    if (
      isOrganizationsPending &&
      !isStaticallySelectableCountry(targetCountryCode)
    ) {
      return;
    }

    const isSelectableCountry = isCountrySelectableInLanguageChanger(
      targetCountryCode,
      evaCountryIds,
    );

    setShouldShow(targetDiffersFromCurrent && isSelectableCountry);
  }, [
    targetCountryCode,
    currentCountryCode,
    evaCountryIds,
    isOrganizationsPending,
  ]);

  const handleClose = useCallback(() => {
    setCookie(DISMISSED_COOKIE_NAME, 'true', {
      maxAge: DISMISSED_COOKIE_MAX_AGE,
      path: '/',
    });
    setShouldShow(false);
  }, []);

  const handleContinueToTargetCountry = useCallback(() => {
    if (!targetCountryCode) return;

    setCookie(DISMISSED_COOKIE_NAME, 'true', {
      maxAge: DISMISSED_COOKIE_MAX_AGE,
      path: '/',
    });

    const targetLocale = resolveLocaleForDetectedCountry(
      targetCountryCode,
      undefined,
      organizations ?? [],
      DEFAULT_LOCALE,
    );

    router.push('/', { locale: targetLocale });
    setShouldShow(false);
  }, [targetCountryCode, organizations, router]);

  if (!shouldShow || !targetCountryCode) {
    return null;
  }

  return (
    <CountryDetectionModal
      targetCountryCode={targetCountryCode}
      currentCountryCode={currentCountryCode}
      onClose={handleClose}
      onContinueToTargetCountry={handleContinueToTargetCountry}
    />
  );
};

export default CountryDetectionModalWrapper;
